<script lang="ts">
    import { toast } from "@zerodevx/svelte-toast";
    import { Button, Checkbox, P, Select, Span } from "flowbite-svelte";
    import { streamTranscribe } from "smartclaim/dist/audio.js";
    import { onDestroy, onMount } from "svelte";
    import { get_azure_token } from "../../service/http_calls";
    import {
        deleteNestedDict,
        getNestedDict,
        updateNestedDict,
    } from "../../service/sse";
    import { createLocalStorageStore, global_state } from "../../service/store";
    import { TrashBinOutline } from "flowbite-svelte-icons";
    import GenericConfirmModal from "../components/GenericConfirmModal.svelte";

    let isRecording = false;
    let transcript = "";
    let interimTranscript = "";
    let includeSystemAudio = false;
    let streamResult = null;
    let isFirefox = false;
    let microphoneDevices = [];
    let selectedMicrophoneId = "";
    let devicePermissionGranted = false;
    let modal_open = false;
    let loadingDevices = false;

    let meeting_id = createLocalStorageStore(
        "realtime_meeting_id",
        crypto.randomUUID(),
    );

    const unsubscribe = global_state.subscribe((state) => {
        transcript = getNestedDict(state, ["realtime", "transcription"]) ?? "";
    });

    // Check if user is on Firefox
    onDestroy(() => {
        unsubscribe();
        if (streamResult) {
            streamResult.stop();
        }
    });

    onMount(async () => {
        await enumerateMicrophoneDevices();
    });

    // Check if browser is Firefox
    function detectFirefox() {
        return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    }

    isFirefox = detectFirefox();

    async function enumerateMicrophoneDevices() {
        loadingDevices = true;
        try {
            // Request microphone permission first
            await navigator.mediaDevices.getUserMedia({ audio: true });
            devicePermissionGranted = true;

            // Get all audio input devices
            const devices = await navigator.mediaDevices.enumerateDevices();
            microphoneDevices = devices
                .filter((device) => device.kind === "audioinput")
                .map((device) => ({
                    value: device.deviceId,
                    name:
                        device.label ||
                        `Microphone ${device.deviceId.slice(0, 8)}...`,
                }));

            // Set default device if none selected
            if (microphoneDevices.length > 0 && !selectedMicrophoneId) {
                selectedMicrophoneId = microphoneDevices[0].value;
            }
        } catch (error) {
            console.error("Error enumerating microphone devices:", error);
            toast.push(
                "Unable to access microphone devices. Please grant microphone permission.",
                {
                    theme: {
                        "--toastBackground": "#F56565",
                        "--toastBarBackground": "#C53030",
                    },
                },
            );
        } finally {
            loadingDevices = false;
        }
    }

    function displayJsonMessage(message) {
        console.log("JSON Message:", message);
    }

    function updateStatus(message, color) {
        console.log(message);
        // Can be implemented to show status in UI
    }

    function resetUI() {
        isRecording = false;
        if (streamResult) {
            streamResult.stop();
            streamResult = null;
        }
    }

    function resetUIControls() {
        isRecording = false;
    }

    function formatTimestamp() {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, "0");
        const minutes = now.getMinutes().toString().padStart(2, "0");
        const seconds = now.getSeconds().toString().padStart(2, "0");
        return `<span class="text-blue-500">[${hours}:${minutes}:${seconds}]</span>`;
    }

    async function startRecording() {
        if (!devicePermissionGranted) {
            toast.push("Please grant microphone permission first.", {
                theme: {
                    "--toastBackground": "#F56565",
                    "--toastBarBackground": "#C53030",
                },
            });
            await enumerateMicrophoneDevices();
            return;
        }

        isRecording = true;
        const token_data = await get_azure_token();
        console.log("got token data", token_data);
        let smartclaimUrl;
        if (window.location.hostname === "localhost") {
            smartclaimUrl = "http://localhost:8083/api";
        } else {
            smartclaimUrl = window.location.origin + "/api";
        }
        const streamConfig = {
            // Azure-specific configuration
            region: token_data.azure_speech_region,
            azureSpeechToken: token_data.azure_speech_token,
            language: "en-US",

            // SmartClaim configuration
            smartclaimAccessToken: token_data.smartclaim_access_token,
            smartclaimUrl: smartclaimUrl,

            // General configuration
            meetingId: $meeting_id,
            includeSystemAudio: includeSystemAudio,
            microphoneDeviceId: selectedMicrophoneId || undefined,

            // Callbacks
            onTranscript: (text, isFinal) => {
                if (isFinal) {
                    // Add timestamp to final transcripts
                    const timestamp = formatTimestamp();
                    const timestampedText = `${timestamp} ${text}`;

                    // For final transcripts, update the global state
                    global_state.update((state) => {
                        updateNestedDict(
                            state,
                            ["realtime", "transcription"],
                            transcript +
                                (transcript ? "\n" : "") +
                                timestampedText,
                        );
                        return state;
                    });

                    // Clear interim transcript
                    interimTranscript = "";
                } else {
                    // For interim transcripts, update local variable
                    interimTranscript = text;
                }
            },
            onEvent: (message) => {
                if (
                    (message.type === "response" &&
                        message.key === "transcription") ||
                    (message.type === "interim" &&
                        message.sub_type === "transcription")
                ) {
                    // These are already handled by onTranscript
                    return; // Skip displaying in JSON panel
                }

                // Display only non-transcription messages in the JSON panel
                if (message.key == "transcript_processed") {
                    displayJsonMessage(message.message);
                    global_state.update((state) => {
                        // Convert projects array to a dictionary with id as key
                        const projectsDict = {};
                        if (Array.isArray(message.message.projects)) {
                            message.message.projects.forEach((project) => {
                                // Process summary data
                                if (project.summary) {
                                    // Convert summary properties to have a data substructure
                                    const processedSummary = {};

                                    // Iterate through each key in the summary
                                    Object.keys(project.summary).forEach(
                                        (key) => {
                                            // Create a data object for each summary item
                                            processedSummary[key] = {
                                                data: {
                                                    content:
                                                        project.summary[key],
                                                    completed: "true",
                                                },
                                            };
                                        },
                                    );
                                    // Replace the original summary with the processed one
                                    project.summary = processedSummary;
                                }

                                if (project && project.id) {
                                    projectsDict[project.id] = project;
                                }
                            });
                        }
                        updateNestedDict(
                            state,
                            ["realtime", "router", "projects", "data"],
                            projectsDict,
                        );

                        return state;
                    });
                }
            },
            onError: (error) => {
                const errorMsg =
                    error.message ||
                    (error.toString ? error.toString() : "Unknown error");
                console.log(`Error: ${errorMsg}`, "message-error");
                console.error("Streaming error:", error);

                // Show notification for specific Azure connection errors
                if (
                    errorMsg.includes("Unable to contact server") ||
                    errorMsg.includes("StatusCode: 1006")
                ) {
                    toast.push(
                        "Your Azure Speech token has expired. Please refresh your token and try again.",
                        {
                            theme: {
                                "--toastBackground": "#F56565",
                                "--toastBarBackground": "#C53030",
                            },
                        },
                    );
                } else {
                    // Show general error notification
                    toast.push(`Error: ${errorMsg}`, {
                        theme: {
                            "--toastBackground": "#F56565",
                            "--toastBarBackground": "#C53030",
                        },
                    });
                }

                updateStatus(`Error: ${errorMsg}`, "#f44336");
                resetUI();
            },
            onClose: () => {
                console.log("Connection closed", "message-info");
                updateStatus("Connection closed", "#888");
                resetUIControls();
            },
        };

        streamResult = await streamTranscribe(streamConfig);
        console.log(streamResult);
    }

    async function stopRecording() {
        if (streamResult) {
            streamResult.stop();
            streamResult = null;
        }
        isRecording = false;
        interimTranscript = "";
    }
</script>

<P size="lg" class="my-3 font-bold">
    <Span gradient>Meeting Transcription</Span>
</P>

<div class="max-w-3xl mx-auto">
    <!-- Controls Section -->
    <div class="bg-white rounded-lg mb-3 mr-3">
        <div class="space-y-2">
            <!-- Microphone Selection and System Audio -->
            <div class="flex items-center justify-between gap-4">
                {#if loadingDevices}
                    <div class="text-blue-600 bg-blue-100 p-2 rounded-md text-sm flex-1 flex items-center gap-2">
                        <svg class="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading microphone devices...</span>
                    </div>
                {:else if microphoneDevices.length > 0}
                    <div class="flex items-center gap-2 flex-1">
                        <label
                            for="microphone-select"
                            class="text-sm font-medium text-gray-700 whitespace-nowrap"
                        >
                            Microphone:
                        </label>
                        <Select
                            id="microphone-select"
                            bind:value={selectedMicrophoneId}
                            disabled={isRecording}
                            class="flex-1"
                        >
                            {#each microphoneDevices as device}
                                <option value={device.value}
                                    >{device.name}</option
                                >
                            {/each}
                        </Select>
                    </div>
                {:else if !devicePermissionGranted}
                    <div
                        class="text-amber-600 bg-amber-100 p-2 rounded-md text-sm flex-1"
                    >
                        <strong>Microphone access required:</strong> Please
                        grant permission.
                        <button
                            class="block mx-auto mt-1 px-3 py-1 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 transition-colors"
                            on:click={enumerateMicrophoneDevices}
                        >
                            Grant Permission
                        </button>
                    </div>
                {/if}

                <div class="flex items-center gap-2 whitespace-nowrap">
                    <Checkbox
                        bind:checked={includeSystemAudio}
                        id="system-audio"
                        disabled={isFirefox}
                    >
                        Capture Meeting Audio
                    </Checkbox>
                </div>
            </div>

            {#if isFirefox}
                <div class="text-amber-600 bg-amber-100 p-2 rounded-md text-sm">
                    <strong>Note:</strong> Including meeting audio is not possible
                    with Firefox. Please use Chrome, Edge, or another Chromium-based
                    browser.
                </div>
            {/if}
        </div>
    </div>

    <!-- Recording Controls -->
    <div class="bg-white rounded-lg p-3">
        <div class="flex flex-row justify-center items-center gap-2">
            <Button
                class={`px-4 ${isRecording ? "bg-red-600 hover:bg-red-700" : "bg-purple-600 hover:bg-purple-700"} text-white flex items-center gap-1.5 transition-colors shadow-sm hover:shadow-md text-sm`}
                on:click={() => {
                    if (!isRecording) {
                        startRecording();
                    } else {
                        stopRecording();
                    }
                }}
            >
                {#if isRecording}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    Stop Recording
                {:else}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    Start Recording
                {/if}
            </Button>
            {#if transcript && transcript.length > 0}
                <Button
                    class="mr-3  hover:bg-purple-200 rounded-full p-2 bg-transparent"
                    title="Clear content and instructions"
                    on:click={() => {
                        modal_open = true;
                    }}
                >
                    <TrashBinOutline color="grey"></TrashBinOutline>
                </Button>
            {/if}
            {#if isRecording}
                <p class="text-xs text-gray-600 flex items-center gap-1.5">
                    <span
                        class="animate-pulse w-1.5 h-1.5 bg-red-500 rounded-full"
                    ></span>
                    Recording in progress...
                </p>
            {/if}
        </div>
    </div>

    <!-- Transcription Display -->
    <div class="bg-white rounded-lg shadow-sm pr-5">
        <div
            class="text-justify font-sans text-gray-800 text-sm whitespace-pre-wrap break-words"
        >
            {@html transcript}{#if interimTranscript}
                <span class="text-gray-400 italic">{interimTranscript}</span>
            {/if}
        </div>

        <div class="flex justify-center mt-2 pt-2">
            {#if $meeting_id}
                <P size="sm" class="text-gray-500 flex items-center gap-2">
                    Meeting ID: <code
                        class="bg-gray-100 px-2 py-0.5 rounded font-mono text-sm"
                        >{$meeting_id}</code
                    >
                    <button
                        class="hover:bg-gray-100 p-1 rounded-full transition-colors"
                        title="Copy meeting ID"
                        on:click={() => {
                            navigator.clipboard.writeText($meeting_id);
                            toast.push("Meeting ID copied to clipboard");
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                    </button>
                </P>
            {:else}
                <P size="sm" class="text-gray-500"
                    >No meeting has been started yet.</P
                >
            {/if}
        </div>
    </div>
</div>
<GenericConfirmModal
    bind:modal_open
    has_delete_narratives={false}
    has_delete_instructions={false}
    use_files={false}
    title="Clear Transcript & Projects"
    message="Are you sure you want to clear the transcript and all projects information? This action cannot be undone."
    on_confirm={() => {
        global_state.update((state) => {
            deleteNestedDict(state, ["realtime", "transcription"]);
            deleteNestedDict(state, ["realtime", "router", "projects", "data"]);
            return state;
        });

        modal_open = false;
        meeting_id.set(crypto.randomUUID());
        window.location.reload();
    }}
></GenericConfirmModal>
